import React, { useState, useEffect } from 'react'
import { useCurrency } from '../context/CurrencyContext'

const FREQ_TO_MONTHLY = { monthly: 1, quarterly: 1 / 3, 'half-yearly': 1 / 6, yearly: 1 / 12 }
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function toMonthly(amount, frequency) {
  return parseFloat(amount || 0) * (FREQ_TO_MONTHLY[frequency] || 1)
}

function fmtDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return MONTHS[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear()
}

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', padding: '0.55rem 0.75rem',
  fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif',
  color: 'var(--text)', width: '100%', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.18s'
}

const labelStyle = {
  display: 'block', fontFamily: 'monospace', fontSize: '0.67rem',
  fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--text-muted)', marginBottom: '0.35rem'
}

// ─── Transaction Modal ────────────────────────────────────────────────────────

function TransactionModal({ type, onSave, onClose }) {
  const [amount, setAmount] = useState('')
  const [notes,  setNotes]  = useState('')
  const [error,  setError]  = useState('')
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount')
      return
    }
    setSaving(true)
    setError('')
    await onSave({ amount: parseFloat(amount), notes: notes.trim() || null })
    setSaving(false)
  }

  const isDeposit = type === 'deposit'
  const accentColor = isDeposit ? '#4a6741' : '#b8441a'

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ background: 'var(--surface)', borderRadius: '10px', border: '1px solid var(--border)', width: '100%', maxWidth: '380px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: accentColor }} />
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700' }}>
            {isDeposit ? '➕ Add Money' : '➖ Withdraw Money'}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1, padding: '0.2rem' }}>✕</button>
        </div>

        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {error && (
            <div style={{ background: 'rgba(184,68,26,0.08)', border: '1px solid rgba(184,68,26,0.2)', borderRadius: '6px', padding: '0.7rem 1rem', fontSize: '0.82rem', color: '#b8441a' }}>
              ⚠️ {error}
            </div>
          )}
          <div>
            <label style={labelStyle}>Amount (₹)</label>
            <input type="text" inputMode="decimal" placeholder="e.g. 5000" value={amount} onChange={e => setAmount(e.target.value)}
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = 'var(--text)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </div>
          <div>
            <label style={labelStyle}>Notes (optional)</label>
            <input type="text" placeholder={isDeposit ? 'e.g. Monthly savings transfer' : 'e.g. Medical emergency'}
              value={notes} onChange={e => setNotes(e.target.value)}
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = 'var(--text)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </div>
        </div>

        <div style={{ padding: '0.9rem 1.5rem 1.3rem', display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '0.5rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ background: accentColor, color: '#fff', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : isDeposit ? 'Add Money' : 'Withdraw'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function EmergencyFund({ fund, transactions, loans, insurancePolicies, expenses, onSaveFund, onAddTransaction, loading }) {
  const { fmt } = useCurrency()
  const [txModal,        setTxModal]        = useState(null)  // 'deposit' | 'withdrawal' | null
  const [targetMonths,   setTargetMonths]   = useState(fund?.target_months || 6)
  const [monthlyContrib, setMonthlyContrib] = useState(fund?.monthly_contribution || '')
  const [savedFlash,     setSavedFlash]     = useState(false)

  // Keep local state in sync when fund loads
  useEffect(() => {
    if (fund) {
      setTargetMonths(fund.target_months || 6)
      setMonthlyContrib(fund.monthly_contribution || '')
    }
  }, [fund])

  // Derived financials
  const totalEMI = loans.reduce((s, l) => s + parseFloat(l.emi_amount || 0), 0)
  const totalInsurance = (insurancePolicies || []).reduce((s, p) => s + toMonthly(p.premium_amount, p.premium_frequency), 0)
  const totalExpenses  = (expenses || []).reduce((s, e) => s + toMonthly(e.amount, e.frequency), 0)
  const monthlyTotal   = totalEMI + totalInsurance + totalExpenses
  const recommended    = monthlyTotal * targetMonths

  const currentBalance  = parseFloat(fund?.current_balance || 0)
  const target          = parseFloat(fund?.target_amount)  || recommended || 0
  const pct             = target > 0 ? Math.min(100, Math.round((currentBalance / target) * 100)) : 0
  const monthsCovered   = monthlyTotal > 0 ? currentBalance / monthlyTotal : 0
  const barColor        = pct >= 100 ? '#4a6741' : pct >= 60 ? '#c9a84c' : '#b8441a'
  const remaining       = Math.max(0, target - currentBalance)
  const monthsToGoal    = monthlyContrib > 0 && remaining > 0 ? Math.ceil(remaining / parseFloat(monthlyContrib)) : null

  async function handleSaveSettings() {
    await onSaveFund({
      target_months:        targetMonths,
      target_amount:        recommended,
      monthly_contribution: parseFloat(monthlyContrib) || 0,
    })
    setSavedFlash(true)
    setTimeout(() => setSavedFlash(false), 2000)
  }

  async function handleTransaction(data) {
    await onAddTransaction({ ...data, type: txModal })
    setTxModal(null)
  }

  if (loading) {
    return (
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
        Loading emergency fund...
      </div>
    )
  }

  return (
    <>
      {txModal && (
        <TransactionModal type={txModal} onSave={handleTransaction} onClose={() => setTxModal(null)} />
      )}

      <div style={{ maxWidth: '820px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

        {/* ── Balance hero ── */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.5rem 1.75rem', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: barColor }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>Emergency Fund Balance</div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '2.6rem', fontWeight: '900', color: barColor, lineHeight: 1 }}>
                {fmt(currentBalance)}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.45rem' }}>
                covers{' '}
                <span style={{ color: barColor, fontWeight: '700' }}>{monthsCovered.toFixed(1)}</span>
                {' '}months of total expenses
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
              <button onClick={() => setTxModal('deposit')}
                style={{ background: '#4a6741', color: '#fff', border: 'none', padding: '0.6rem 1.1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '600', cursor: 'pointer', transition: 'opacity 0.15s' }}
                onMouseEnter={e => e.target.style.opacity = '0.82'}
                onMouseLeave={e => e.target.style.opacity = '1'}>
                ➕ Add Money
              </button>
              <button onClick={() => setTxModal('withdrawal')}
                style={{ background: 'transparent', color: '#b8441a', border: '1.5px solid #b8441a', padding: '0.6rem 1.1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#b8441a'; e.currentTarget.style.color = '#fff' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#b8441a' }}>
                ➖ Withdraw
              </button>
            </div>
          </div>

          {/* Progress */}
          <div style={{ marginBottom: '0.55rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.4rem' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '0.63rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Progress toward {targetMonths}-month target
              </span>
              <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', color: barColor }}>{pct}%</span>
            </div>
            <div style={{ height: '10px', background: 'var(--surface-hdr)', borderRadius: '99px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: pct + '%', background: barColor, borderRadius: '99px', transition: 'width 0.8s ease' }} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span>Saved: <span style={{ color: barColor, fontWeight: '600' }}>{fmt(currentBalance)}</span></span>
            <span>Target: <span style={{ color: 'var(--text)', fontWeight: '600' }}>{fmt(target)}</span></span>
            <span>
              {currentBalance >= target
                ? <span style={{ color: '#4a6741', fontWeight: '600' }}>🎉 Goal reached!</span>
                : <>Remaining: <span style={{ color: '#b8441a', fontWeight: '600' }}>{fmt(remaining)}</span></>}
            </span>
          </div>
        </div>

        {/* ── Two-column: outflow + calculator ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

          {/* Monthly outflow breakdown */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '0.9rem 1.3rem' }}>
              <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700' }}>Monthly Outflow</span>
            </div>
            <div style={{ padding: '0.6rem 1.3rem 1rem' }}>
              {[
                { label: 'Loan EMIs',          value: totalEMI,        color: '#b8441a' },
                { label: 'Insurance Premiums', value: totalInsurance,   color: '#3d4a5c' },
                { label: 'Living Expenses',    value: totalExpenses,    color: '#c9a84c' },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.5rem 0', borderBottom: '1px solid var(--surface-hdr)' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{r.label}</span>
                  <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '600', color: r.color }}>{fmt(r.value)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingTop: '0.6rem', marginTop: '0.1rem' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text)' }}>Total / Month</span>
                <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: 'var(--text)' }}>{fmt(monthlyTotal)}</span>
              </div>
              {monthlyTotal === 0 && (
                <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: '0.5rem', lineHeight: 1.5 }}>
                  Add your loans, insurance policies and expenses to auto-calculate your monthly outflow.
                </div>
              )}
            </div>
          </div>

          {/* Target calculator */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '0.9rem 1.3rem' }}>
              <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700' }}>Target Calculator</span>
            </div>
            <div style={{ padding: '1rem 1.3rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              <div>
                <div style={{ ...labelStyle, marginBottom: '0.4rem' }}>Months of coverage goal</div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  {[3, 6, 12].map(m => (
                    <button key={m} onClick={() => setTargetMonths(m)}
                      style={{ flex: 1, padding: '0.42rem 0', borderRadius: '4px', border: '1.5px solid', borderColor: targetMonths === m ? '#c9a84c' : 'var(--border)', background: targetMonths === m ? 'rgba(201,168,76,0.1)' : 'transparent', color: targetMonths === m ? '#c9a84c' : 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: targetMonths === m ? '700' : '400', cursor: 'pointer', transition: 'all 0.15s' }}>
                      {m}mo
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ background: 'var(--surface-hdr)', borderRadius: '6px', padding: '0.75rem 0.9rem' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', marginBottom: '0.2rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Recommended Target</div>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.5rem', fontWeight: '700', color: '#c9a84c' }}>{fmt(recommended)}</div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                  {fmt(monthlyTotal)}/mo × {targetMonths} months
                </div>
              </div>

              <div>
                <div style={labelStyle}>Monthly contribution goal</div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <input type="text" inputMode="decimal" placeholder="e.g. 5000"
                    value={monthlyContrib} onChange={e => setMonthlyContrib(e.target.value)}
                    style={{ flex: 1, background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.45rem 0.6rem', fontSize: '0.82rem', fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', outline: 'none' }}
                    onFocus={e => e.target.style.borderColor = 'var(--text)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                  <button onClick={handleSaveSettings}
                    style={{ background: savedFlash ? '#4a6741' : 'var(--btn-bg)', color: savedFlash ? '#fff' : 'var(--btn-text)', border: 'none', padding: '0.45rem 0.75rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.3s' }}>
                    {savedFlash ? '✓ Saved' : 'Save'}
                  </button>
                </div>
                {monthsToGoal !== null && (
                  <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                    At {fmt(parseFloat(monthlyContrib))}/mo → goal in{' '}
                    <span style={{ color: '#4a6741', fontWeight: '600' }}>{monthsToGoal} month{monthsToGoal !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Coverage milestones ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          {[3, 6, 12].map(m => {
            const needed   = monthlyTotal * m
            const achieved = currentBalance >= needed && needed > 0
            const mpct     = needed > 0 ? Math.min(100, Math.round((currentBalance / needed) * 100)) : 0
            const mColor   = achieved ? '#4a6741' : mpct >= 50 ? '#c9a84c' : 'var(--border)'
            return (
              <div key={m} style={{ background: 'var(--surface)', border: `1px solid ${achieved ? '#4a6741' : 'var(--border)'}`, borderRadius: '8px', padding: '1rem 1.2rem', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: mColor }} />
                <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: achieved ? '#4a6741' : 'var(--text-muted)', marginBottom: '0.25rem' }}>
                  {achieved ? '✓' : '○'} {m}-Month Buffer
                </div>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', color: achieved ? '#4a6741' : 'var(--text)', marginBottom: '0.25rem' }}>
                  {fmt(needed)}
                </div>
                <div style={{ height: '4px', background: 'var(--surface-hdr)', borderRadius: '99px', overflow: 'hidden', marginBottom: '0.3rem' }}>
                  <div style={{ height: '100%', width: mpct + '%', background: mColor, borderRadius: '99px', transition: 'width 0.6s ease' }} />
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>
                  {achieved ? 'Achieved 🎉' : needed > 0 ? `${fmt(Math.max(0, needed - currentBalance))} to go` : '—'}
                </div>
              </div>
            )
          })}
        </div>

        {/* ── Transaction history ── */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.4rem', background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Transaction History</span>
            <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
              {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
            </span>
          </div>

          {transactions.length === 0 ? (
            <div style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.75rem' }}>
              No transactions yet. Click <strong>Add Money</strong> to start building your fund.
            </div>
          ) : (
            transactions.map((tx, i) => {
              const isDeposit = tx.type === 'deposit'
              return (
                <div key={tx.id}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.85rem 1.4rem', borderBottom: i < transactions.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDeposit ? 'rgba(74,103,65,0.12)' : 'rgba(184,68,26,0.1)', color: isDeposit ? '#4a6741' : '#b8441a', fontSize: '1rem', fontWeight: '700', flexShrink: 0 }}>
                      {isDeposit ? '↑' : '↓'}
                    </div>
                    <div>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '500', color: 'var(--text)', marginBottom: '0.1rem' }}>
                        {isDeposit ? 'Deposit' : 'Withdrawal'}
                        {tx.notes && (
                          <span style={{ color: 'var(--text-muted)', fontWeight: '400' }}> — {tx.notes}</span>
                        )}
                      </div>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>{fmtDate(tx.created_at)}</div>
                    </div>
                  </div>

                  <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: isDeposit ? '#4a6741' : '#b8441a', flexShrink: 0 }}>
                    {isDeposit ? '+' : '−'}{fmt(tx.amount)}
                  </div>
                </div>
              )
            })
          )}
        </div>

      </div>
    </>
  )
}
